import React from "react";

import { CardModalBlock, CardBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { Clickable } from "../Clickables";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { CardModal } from "./CardModal";

import styles from "./Card.module.scss";

interface Props {
    value: Value;
}

const CardFaceMedia = ({ value }: { value: Value }) =>
    value.media ? (
        <MediaChooserBlock
            value={value.media}
            wrapperAttrs={{ className: styles.media }}
            imageProps={{ sizes: styles.sizes }}
        />
    ) : null;

const CardCaption = ({ value }: { value: Value }) => (
    <>
        <span className={styles.title}>{value.title}</span>
        {value.subtitle && (
            <span className={styles.subtitle}>{value.subtitle}</span>
        )}
    </>
);

const ModalCard = ({
    value,
    modal,
}: {
    value: Value;
    modal: CardModalBlock;
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const open = () => setIsOpen(true);

    return (
        <li
            className={concatClassNames([styles.card, styles.clickable])}
            onClick={open}
        >
            <CardFaceMedia value={value} />
            <Clickable
                className={styles.caption}
                onPress={open}
                aria-haspopup="dialog"
            >
                <CardCaption value={value} />
            </Clickable>
            <CardModal
                value={modal}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                label={value.title ?? undefined}
            />
        </li>
    );
};

export const Card = ({ value }: Props) => {
    if (!value.media) return null;

    const linkUrl = value.link?.url;

    if (value.modal?.content?.length) {
        return <ModalCard value={value} modal={value.modal} />;
    }

    // A link card.
    if (linkUrl) {
        return (
            <li
                className={concatClassNames([styles.card, styles.clickable])}
                onClick={delegateClickToLink}
            >
                <CardFaceMedia value={value} />
                <Clickable
                    simpleLink={value.link ?? undefined}
                    className={styles.caption}
                >
                    <CardCaption value={value} />
                </Clickable>
            </li>
        );
    }

    // A static card: no interaction.
    return (
        <li className={styles.card}>
            <CardFaceMedia value={value} />
            <div className={styles.caption}>
                <CardCaption value={value} />
            </div>
        </li>
    );
};
